import React, { createContext, useReducer, useContext, isValidElement } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

type State = { id: string; message: string | JSX.Element; show: boolean }[];
type Action =
  | { type: 'pop'; id: string }
  | { type: 'hide'; id: string }
  | { type: 'push'; id: string; message: string | JSX.Element };

const initialState: State = [];

function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'push':
      return [...state, { id: action.id, message: action.message, show: true }];
    case 'hide':
      // eslint-disable-next-line no-case-declarations
      const hideIndex = state.findIndex(notification => notification.id === action.id);
      // eslint-disable-next-line no-case-declarations
      const modifiedNotification = { ...state[hideIndex], show: false };
      return [...state.slice(0, hideIndex), modifiedNotification, ...state.slice(hideIndex + 1)];
    case 'pop':
      // eslint-disable-next-line no-case-declarations
      const poppedIndex = state.findIndex(notification => notification.id === action.id);
      if (poppedIndex === state.length - 1) {
        return state.slice(0, -1);
      }
      if (poppedIndex === 0) {
        return state.slice(1);
      }
      return state.slice(0, poppedIndex).concat(state.slice(poppedIndex + 1));
  }
}

type PushNotification = (message: string | JSX.Element) => void;
const NotificationsDispatch = createContext<PushNotification | undefined>(undefined);

export const usePushNotifications = () => {
  const ctx = useContext(NotificationsDispatch);
  if (ctx === undefined) throw new Error('Notification Services is used out of its scope');
  return ctx;
};

interface Props {
  children: React.ReactNode | React.ReactNode[];
}

export function NotificationService({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pushNotification = (message: string | JSX.Element) =>
    dispatch({
      type: 'push',
      id: String(Date.now()),
      message
    });
  return (
    <NotificationsDispatch.Provider value={pushNotification}>
      {state.map(notification => (
        <Snackbar
          key={notification.id}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          open={notification.show}
          onClose={() => dispatch({ type: 'hide', id: notification.id })}
          onExited={() => dispatch({ type: 'pop', id: notification.id })}
          autoHideDuration={15000}
        >
          {isValidElement(notification.message) ? (
            notification.message
          ) : (
            <SnackbarContent
              key={notification.id}
              aria-describedby="client-snackbar"
              message={<span>{notification.message}</span>}
              action={[
                <IconButton
                  key="close"
                  aria-label="close"
                  color="inherit"
                  onClick={() => dispatch({ type: 'pop', id: notification.id })}
                >
                  <CloseIcon />
                </IconButton>
              ]}
            />
          )}
        </Snackbar>
      ))}
      {children}
    </NotificationsDispatch.Provider>
  );
}
